/**
 * Формирование XLSX с одним листом: XML книги, стилей и ячеек создается здесь,
 * а fflate упаковывает эти части в ZIP, из которого состоит файл XLSX.
 */
import {Zip, ZipDeflate, ZipPassThrough, strToU8} from "fflate";

const XML_HEADER = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>';
const SHEET_NS = "http://schemas.openxmlformats.org/spreadsheetml/2006/main";
const REL_NS = "http://schemas.openxmlformats.org/officeDocument/2006/relationships";
export const XLSX_MIME_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

/**
 * Подготавливает текст для XML и правил экранирования SpreadsheetML.
 *
 * @param {*} value
 * @return {String}
 */
function xmlText(value) {
  return String(value ?? "")
    // XML 1.0 запрещает эти управляющие символы в текстовых узлах.
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\uFFFE\uFFFF]/g, "")
    // Сохраняем парные суррогаты Unicode (например, эмодзи), удаляем одиночные.
    .replace(/[\uD800-\uDBFF][\uDC00-\uDFFF]|[\uD800-\uDFFF]/g, match => match.length === 2 ? match : "")
    // Защищаем буквальный текст _xHHHH_ от преобразования Excel в символ, затем экранируем XML.
    .replace(/_x[\da-f]{4}_/gi, match => `_x005F_${match.slice(1)}`)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/**
 * Преобразует индекс столбца с нуля в обозначение Excel: A, ..., Z, AA и далее.
 *
 * @param {Number} index
 * @return {String}
 */
function columnName(index) {
  let name = "";
  for (let n = index + 1; n > 0; n = Math.floor((n - 1) / 26)) {
    name = String.fromCharCode(65 + (n - 1) % 26) + name;
  }
  return name;
}

/**
 * Формирует ячейку с сохранением типа числа или логического значения.
 * Остальные значения записываются текстом: ведущие нули сохраняются, формулы не выполняются.
 *
 * @param {*} value Значение ячейки; дата записывается текстом в формате ISO.
 * @param {String} reference Адрес ячейки, например A2.
 * @param {Boolean} isHeader Использовать жирный шрифт заголовка.
 * @return {String}
 */
function cellXML(value, reference, isHeader) {
  const style = isHeader ? ' s="1"' : "";
  if (typeof value === "number" && Number.isFinite(value)) {
    return `<c r="${reference}"${style}><v>${value}</v></c>`;
  }
  if (typeof value === "boolean") {
    return `<c r="${reference}" t="b"${style}><v>${value ? 1 : 0}</v></c>`;
  }
  const text = value instanceof Date ? value.toISOString() : String(value ?? "");
  // Excel допускает не более 32767 символов текста в одной ячейке.
  if (text.length > 32767) throw new Error("XLSX cell text exceeds 32767 characters");
  return `<c r="${reference}" t="inlineStr"${style}><is><t xml:space="preserve">${xmlText(text)}</t></is></c>`;
}

/**
 * Создает отчет с одним листом и сразу добавляет строку заголовков.
 * Данные добавляются порциями; между ними сохраняются фрагменты ZIP и состояние сжатия,
 * без накопления исходных строк и XML всего листа.
 *
 * @param {Array.<String>} headers Заголовки столбцов в порядке выгрузки.
 * @return {Object} Генератор с методами appendRows, finish и abort.
 */
export function createXLSXReport(headers) {
  // Максимум столбцов на одном листе Excel — 16384.
  if (!Array.isArray(headers) || !headers.length || headers.length > 16384) {
    throw new Error("Invalid XLSX columns");
  }
  const chunks = [];
  let zipError;
  let complete = false;
  let closed = false;
  let rowNumber = 0;
  const zip = new Zip((error, chunk, final) => {
    if (error) zipError = error;
    else chunks.push(chunk);
    if (final) complete = true;
  });
  // Небольшие служебные XML добавляются без сжатия: типы частей, связи, книга и стили.
  function addPart(name, xml) {
    const part = new ZipPassThrough(name);
    zip.add(part);
    part.push(strToU8(XML_HEADER + xml), true);
  }
  addPart("[Content_Types].xml", '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">'
    + '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>'
    + '<Default Extension="xml" ContentType="application/xml"/>'
    + '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>'
    + '<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>'
    + '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/></Types>');
  addPart("_rels/.rels", '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">'
    + `<Relationship Id="rId1" Type="${REL_NS}/officeDocument" Target="xl/workbook.xml"/></Relationships>`);
  addPart("xl/workbook.xml", `<workbook xmlns="${SHEET_NS}" xmlns:r="${REL_NS}">`
    + '<sheets><sheet name="Report" sheetId="1" r:id="rId1"/></sheets></workbook>');
  addPart("xl/_rels/workbook.xml.rels", '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">'
    + `<Relationship Id="rId1" Type="${REL_NS}/worksheet" Target="worksheets/sheet1.xml"/>`
    + `<Relationship Id="rId2" Type="${REL_NS}/styles" Target="styles.xml"/></Relationships>`);
  addPart("xl/styles.xml", `<styleSheet xmlns="${SHEET_NS}">`
    + '<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts>'
    + '<fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills>'
    + '<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>'
    + '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>'
    + '<cellXfs count="2"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>'
    + '<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/></cellXfs>'
    + '<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles></styleSheet>');

  // Лист сжимается по мере добавления страниц; уровень 1 уменьшает нагрузку на браузер.
  const sheet = new ZipDeflate("xl/worksheets/sheet1.xml", {level: 1});
  zip.add(sheet);
  const columns = headers.map((header, index) => {
    const width = Math.min(45, Math.max(20, String(header).length + 4));
    return `<col min="${index + 1}" max="${index + 1}" width="${width}" customWidth="1"/>`;
  }).join("");
  sheet.push(strToU8(XML_HEADER + `<worksheet xmlns="${SHEET_NS}">`
    + '<sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>'
    + `<cols>${columns}</cols><sheetData>`));

  /**
   * Преобразует порцию строк в XML и передает байты UTF-8 в поток сжатия листа.
   *
   * @param {Array.<Array.<*>>} rows Строки с тем же количеством ячеек, что и заголовки.
   * @param {Boolean} isHeader Оформить строки как заголовки.
   */
  function appendRows(rows, isHeader = false) {
    if (closed) throw new Error("XLSX writer is closed");
    const xml = rows.map(row => {
      // Лимит листа Excel — 1048576 строк, включая строку заголовков.
      if (!Array.isArray(row) || row.length !== headers.length || rowNumber >= 1048576) {
        throw new Error("Invalid XLSX row");
      }
      rowNumber += 1;
      return `<row r="${rowNumber}">${row.map((value, index) => cellXML(value, `${columnName(index)}${rowNumber}`, isHeader)).join("")}</row>`;
    }).join("");
    if (xml) sheet.push(strToU8(xml));
    if (zipError) throw zipError;
  }
  appendRows([headers], true);

  return {
    appendRows,
    /**
     * Закрывает XML листа и ZIP, возвращает готовый файл и освобождает массив фрагментов.
     *
     * @return {Blob}
     */
    finish() {
      if (closed) throw new Error("XLSX writer is closed");
      closed = true;
      sheet.push(strToU8(`</sheetData><autoFilter ref="A1:${columnName(headers.length - 1)}${rowNumber}"/></worksheet>`), true);
      zip.end();
      if (zipError) throw zipError;
      if (!complete) throw new Error("XLSX ZIP was not completed");
      const blob = new Blob(chunks, {type: XLSX_MIME_TYPE});
      chunks.length = 0;
      return blob;
    },
    /**
     * Останавливает генератор и удаляет фрагменты при отмене или ошибке.
     * Частичный файл при этом не создается.
     */
    abort() {
      closed = true;
      zip.terminate();
      chunks.length = 0;
    },
  };
}
